import {
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Popconfirm,
  Space,
  Tag,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationEnrollmentSelect } from '@/pages/education/components/EducationLookupSelects';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';

type ApiError = { response?: { data?: { message?: string } } };

type Certificate = {
  id: string;
  tc_number?: string | null;
  status: string;
  school_name?: string | null;
  student_name?: string | null;
  admission_no?: string | null;
  last_grade?: string | null;
  admitted_on?: string | null;
  exit_on?: string | null;
  signatory_name?: string | null;
  issued_on?: string | null;
  verification_ref?: string | null;
  declarations_json?: string | null;
  record_version?: number;
  can_edit?: boolean;
};

function statusColor(status?: string): string | undefined {
  if (status === 'issued') return 'green';
  return 'default';
}

function dateValue(value?: string | null): string | undefined {
  if (!value) return undefined;
  return String(value).slice(0, 10);
}

export default function TransferCertificateDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canIssue = Boolean(
    useAuthStore((state) => state.user?.permissions?.includes('education.transfer_certificate.issue'))
  );
  const isNew = !id || id === 'new';
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [reviseForm] = Form.useForm();

  const query = useQuery({
    queryKey: ['education-tc', id],
    enabled: !isNew,
    queryFn: () => api.get<Certificate>(`/education/transfer-certificates/${id}`),
  });
  const certificate = query.data?.data;
  const canEdit = Boolean(canIssue && (isNew || certificate?.can_edit));

  useEffect(() => {
    if (!certificate) return;
    editForm.setFieldsValue({
      school_name: certificate.school_name,
      student_name: certificate.student_name,
      admission_no: certificate.admission_no,
      last_grade: certificate.last_grade,
      admitted_on: dateValue(certificate.admitted_on),
      exit_on: dateValue(certificate.exit_on),
      signatory_name: certificate.signatory_name,
      declarations_json: certificate.declarations_json || '{}',
      record_version: certificate.record_version,
    });
  }, [certificate, editForm]);

  const create = useMutation({
    mutationFn: (values: Record<string, string>) =>
      api.post<{ id: string }>('/education/transfer-certificates', values),
    onSuccess: (result) => {
      message.success(t('transferCertificateCreated', { defaultValue: 'Transfer certificate drafted' }));
      navigate(`/education/transfer-certificates/${result.data?.id}`);
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const save = useMutation({
    mutationFn: (values: Record<string, string | number>) =>
      api.patch<Certificate>(`/education/transfer-certificates/${id}`, values),
    onSuccess: () => {
      message.success(t('saved', { defaultValue: 'Saved' }));
      queryClient.invalidateQueries({ queryKey: ['education-tc', id] });
      queryClient.invalidateQueries({ queryKey: ['education-transfer-certificates'] });
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const remove = useMutation({
    mutationFn: () =>
      api.delete(`/education/transfer-certificates/${id}`, {
        data: { record_version: certificate?.record_version },
      }),
    onSuccess: () => {
      message.success(t('deleted', { defaultValue: 'Deleted' }));
      navigate('/education/transfer-certificates');
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const issue = useMutation({
    mutationFn: () =>
      api.post(`/education/transfer-certificates/${id}/issue`, {
        record_version: certificate?.record_version,
      }),
    onSuccess: () => {
      message.success(t('certificateIssued'));
      queryClient.invalidateQueries({ queryKey: ['education-tc', id] });
      queryClient.invalidateQueries({ queryKey: ['education-transfer-certificates'] });
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const revise = useMutation({
    mutationFn: (values: { reason: string }) =>
      api.post<{ id: string }>(`/education/transfer-certificates/${id}/revise`, values),
    onSuccess: (result) => {
      message.success(t('reviseCertificate'));
      navigate(`/education/transfer-certificates/${result.data?.id}`);
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const title = useMemo(() => {
    if (isNew) return t('newTransferCertificate', { defaultValue: 'New transfer certificate' });
    return t('transferCertificate');
  }, [isNew, t]);

  return (
    <EducationPageChrome
      title={title}
      description={t('transferCertificateIntro')}
      tags={
        !isNew ? (
          <Tag color={statusColor(certificate?.status)}>{certificate?.status || '—'}</Tag>
        ) : undefined
      }
      extra={
        <Space wrap>
          <Button onClick={() => navigate('/education/transfer-certificates')}>{t('backToCertificates')}</Button>
          {!isNew && id ? (
            <ReportDocumentActions
              reportKey="education-transfer-certificate"
              params={{ id }}
              documentId={id}
              size="small"
              allowPreprinted
              sendPermissions={['education.transfer_certificate.issue', 'education.admin']}
              emailLabel={t('email', { defaultValue: 'Email' })}
            />
          ) : null}
          {!isNew && canIssue && certificate?.status === 'draft' ? (
            <Button type="primary" loading={issue.isPending} onClick={() => issue.mutate()}>
              {t('issueCertificate')}
            </Button>
          ) : null}
          {!isNew && canIssue && certificate?.status === 'draft' ? (
            <Popconfirm
              title={t('deleteTransferCertificateConfirm', {
                defaultValue: 'Delete this draft transfer certificate?',
              })}
              onConfirm={() => remove.mutate()}
            >
              <Button danger loading={remove.isPending}>
                {t('delete')}
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {isNew ? (
          <Card size="small" title={t('createCertificate', { defaultValue: 'Draft certificate' })}>
            <Form
              form={createForm}
              layout="vertical"
              onFinish={(values) => create.mutate(values)}
              style={{ maxWidth: 560 }}
            >
              <Form.Item name="enrollment_id" label={t('enrollmentNo')} rules={[{ required: true }]}>
                <EducationEnrollmentSelect />
              </Form.Item>
              <Form.Item name="school_name" label={t('schoolName')} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="student_name" label={t('name')} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="admission_no" label={t('admissionNo')} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="last_grade" label={t('lastGrade')} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="admitted_on" label={t('admittedOn', { defaultValue: 'Admitted on' })}>
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
              <Form.Item name="exit_on" label={t('exitOn')} rules={[{ required: true }]}>
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
              <Form.Item name="signatory_name" label={t('signatory')} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="declarations_json" label={t('declarations', { defaultValue: 'Declarations JSON' })}>
                <Input.TextArea rows={3} placeholder="{}" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={create.isPending} disabled={!canIssue}>
                {t('save')}
              </Button>
            </Form>
          </Card>
        ) : (
          <>
            <Card size="small" title={t('certificateDetails', { defaultValue: 'Certificate details' })} loading={query.isLoading}>
              <Descriptions column={{ xs: 1, md: 2 }} size="small" style={{ marginBottom: 16 }}>
                <Descriptions.Item label={t('transferCertificate')}>
                  {certificate?.tc_number || t('notIssued')}
                </Descriptions.Item>
                <Descriptions.Item label={t('status')}>
                  <Tag color={statusColor(certificate?.status)}>{certificate?.status || '—'}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('issuedOn', { defaultValue: 'Issued on' })}>
                  {dateValue(certificate?.issued_on) || '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('verificationRef', { defaultValue: 'Verification ref' })}>
                  {certificate?.verification_ref || '—'}
                </Descriptions.Item>
              </Descriptions>
              <Form
                form={editForm}
                layout="vertical"
                onFinish={(values) => save.mutate(values)}
                style={{ maxWidth: 640 }}
                disabled={!canEdit}
              >
                <Form.Item name="record_version" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="school_name" label={t('schoolName')} rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="student_name" label={t('name')} rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="admission_no" label={t('admissionNo')} rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="last_grade" label={t('lastGrade')} rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="admitted_on" label={t('admittedOn', { defaultValue: 'Admitted on' })}>
                  <Input placeholder="YYYY-MM-DD" />
                </Form.Item>
                <Form.Item name="exit_on" label={t('exitOn')} rules={[{ required: true }]}>
                  <Input placeholder="YYYY-MM-DD" />
                </Form.Item>
                <Form.Item name="signatory_name" label={t('signatory')} rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                {canIssue ? (
                  <Form.Item name="declarations_json" label={t('declarations', { defaultValue: 'Declarations JSON' })}>
                    <Input.TextArea rows={3} />
                  </Form.Item>
                ) : null}
                {canEdit ? (
                  <Button type="primary" htmlType="submit" loading={save.isPending}>
                    {t('save')}
                  </Button>
                ) : null}
              </Form>
            </Card>

            {canIssue && certificate?.status === 'issued' ? (
              <Card size="small" title={t('reviseCertificate')}>
                <Form
                  form={reviseForm}
                  layout="vertical"
                  onFinish={(values) => revise.mutate(values)}
                  style={{ maxWidth: 420 }}
                >
                  <Form.Item name="reason" label={t('reason')} rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Button htmlType="submit" loading={revise.isPending}>
                    {t('reviseCertificate')}
                  </Button>
                </Form>
              </Card>
            ) : null}
          </>
        )}
      </Space>
    </EducationPageChrome>
  );
}
